import type { PublishedTmsEventSubject, TmsEventKindOfSubject } from "@storyboard/tms-event-contracts/tmsEventSubjects";
import { tmsEventSubjectOf } from "@storyboard/tms-event-contracts/tmsEventSubjects";
import type { TmsEvent } from "@storyboard/web-client";

/** The DTO arms one subject states, so a field named per subject must exist on every one of its kinds. */
type EventOfSubject<S extends PublishedTmsEventSubject> = Extract<TmsEvent, { kind: TmsEventKindOfSubject<S> }>;

/**
 * Which field, per subject, names the driver directly.
 *
 * Per subject rather than per kind, because the answer is a property of the subject: which references its kinds carry
 * is decided once, in the subject's `refs`, and every kind inherits it. A `StopEvent` names who completed the task;
 * every other subject names the driver it is about. Total over the published subjects, and each field bounded by the
 * keys every kind of that subject shares, so a subject whose kinds disagree cannot be placed. `null` places a subject
 * the feed must not show: stored, indexed under nobody, drawn nowhere.
 */
const DIRECT_MEMBERSHIP_FIELD: { [S in PublishedTmsEventSubject]: keyof EventOfSubject<S> | null } = {
  dutyStatusChange: "workspaceMembershipId",
  formSubmission: "workspaceMembershipId",
  hosClockViolation: "workspaceMembershipId",
  routeAssignmentEvent: "workspaceMembershipId",
  stopEvent: "completedByWorkspaceMembershipId",
};

/**
 * The membership an event names directly, or null when it names none — a vehicle-scoped event whose driver is only
 * known through the assignment intervals (WEB-2276).
 */
export function directMembershipIdOf(event: TmsEvent): string | null {
  const field = DIRECT_MEMBERSHIP_FIELD[tmsEventSubjectOf(event.kind)];
  if (field === null) return null;
  const value: unknown = event[field as keyof TmsEvent];
  return typeof value === "string" && value.length > 0 ? value : null;
}
